import { ArrowUpIcon } from "@chakra-ui/icons";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Flex, FormControl, Input } from "@chakra-ui/react";
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FieldProps } from "@rjsf/utils";
import { useMemo } from "react";
import React, { useState } from "react";
import * as XLSX from "xlsx";

import { MonitoringTube } from "@nens/brostar/openapi-client";
import { ColumnDefinition } from "@nens/chakra/components";
import { TrashIcon } from "@nens/ranalib";
import { downloadFile } from "@nens/shared-utils";

import { TableOverview } from "@/components/table-overview";
import { GmwView, useGmwContext } from "@/features/overview";
import { renderDefault } from "@/helpers/table-render-functions";
import { useGmwMonitoringtubesAllPagesQuery } from "@/queries/query-gmw-monitoringtubes";

export type RegistrationMeasuringPoint = {
  measuringPointCode?: string;
  broId?: string;
  tubeNumber?: string | null;
  tubeStatus?: string | null; // this can also be null
};

const monitoringTubeInformation: { key: keyof RegistrationMeasuringPoint; label: string }[] = [
  { key: "tubeNumber", label: "Buis nummer" },
  { key: "tubeStatus", label: "Buis status" },
  { key: "broId", label: "GMW" },
];

const columnDefinitions: ColumnDefinition<RegistrationMeasuringPoint>[] =
  monitoringTubeInformation.map(({ key, label }) => ({
    title: label,
    renderFunction: (row) => renderDefault(row[key]),
    width: "1fr",
  }));

type MeasuringPointWithTube = RegistrationMeasuringPoint & Partial<MonitoringTube>;

const getOrCreateMeasuringPointCode = (tube: MonitoringTube, broId: string): string =>
  "measuringpointCode" in tube ? tube.measuringpointCode + "" : `${broId}_${tube.tubeNumber}_mp`;

const SelectMonitoringTubesTables = ({
  onChange,
  formData,
}: FieldProps<RegistrationMeasuringPoint[]>) => {
  const { selectedGmw } = useGmwContext();
  const gmwMonitoringtubesQuery = useGmwMonitoringtubesAllPagesQuery({
    gmwBroId: selectedGmw?.broId,
  });

  const selectedTubes: MeasuringPointWithTube[] = useMemo(() => {
    const allTubesFromQueries = gmwMonitoringtubesQuery.data?.results ?? [];
    return (formData ?? []).map((mp) => ({
      ...allTubesFromQueries.find(
        (tube) => tube.gmwBroId === mp.broId && tube.tubeNumber === mp.tubeNumber
      ),
      ...mp,
    }));
  }, [formData, gmwMonitoringtubesQuery.data?.results]);
  const availableTubes = useMemo(() => {
    if (!gmwMonitoringtubesQuery.data || !selectedGmw) return [];
    const allTubes = gmwMonitoringtubesQuery.data.results;
    return allTubes.filter(
      (tube) =>
        !(formData ?? []).find(
          (mp) => mp.broId === (tube.gmwBroId as string) && mp.tubeNumber === tube.tubeNumber
        )
    );
  }, [gmwMonitoringtubesQuery.data, selectedGmw, formData]);

  // Add tube to formData
  const addTube = (tube: MonitoringTube) => {
    const broId = "broId" in tube ? (tube.broId as string) : (selectedGmw?.broId as string);
    const measuringPointCode = getOrCreateMeasuringPointCode(tube, broId);
    const newMp: RegistrationMeasuringPoint = {
      ...tube,
      tubeNumber: tube.tubeNumber + "",
      broId: broId,
      measuringPointCode,
      tubeStatus: tube.tubeStatus ?? null,
    };
    onChange([...(formData ?? []), newMp]);
  };

  // Remove tube from formData
  const removeTube = (row: RegistrationMeasuringPoint) => {
    onChange(
      (formData ?? []).filter((mp) => !(mp.broId === row.broId && mp.tubeNumber === row.tubeNumber))
    );
  };

  // Change measuringPointCode in formData
  const setMeasuringPointCode = (row: RegistrationMeasuringPoint, value: string) => {
    onChange(
      (formData ?? []).map((mp) => {
        if (mp.broId === row.broId && mp.tubeNumber === row.tubeNumber) {
          return { ...mp, measuringPointCode: value };
        }
        return mp;
      })
    );
  };

  // Columns for selected tubes (formData)
  const columnDefinitionsSelect: ColumnDefinition<MeasuringPointWithTube>[] = [
    {
      title: "Meetpunt code",
      renderFunction: (row) => {
        const valueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
          setMeasuringPointCode(row, event.target.value);
        };
        return (
          <FormControl isRequired>
            <Input
              name={row.broId || ""}
              id={row.tubeNumber || ""}
              autoComplete="off"
              placeholder="Code"
              onChange={valueChanged}
              value={row.measuringPointCode || ""}
              type={"text"}
              required
              title={row.measuringPointCode || ""}
            />
          </FormControl>
        );
      },
      width: "2fr",
    },
    ...columnDefinitions,
  ];

  return (
    <Flex flexDir="column" position="relative" w="100%">
      {/* Selected tubes table (formData) */}
      <TableOverview
        paginationProps={undefined}
        columnDefinitions={columnDefinitionsSelect}
        tableData={selectedTubes}
        emptyText={
          "Geen monitoringbuizen geselecteeerd. Klik op een GMW en dan een monitoringbuis."
        }
        quickRowAction={{
          title: "Verwijder uit geselecteerde monitoringbuizen",
          action: removeTube,
          icon: <TrashIcon />,
        }}
      />

      {/* Available tubes table (API, not in formData) */}
      {selectedGmw && (
        <TableOverview
          paginationProps={undefined}
          columnDefinitions={columnDefinitions} // measuringPointCode is added during the addTube action, and is not required in this column definition, but only in the selected tubes table
          isLoading={gmwMonitoringtubesQuery.isLoading}
          emptyText={
            availableTubes.length === 0
              ? "Alle monitoringsbuizen zijn toegevoegd"
              : "Geen GMW geselecteerd"
          }
          quickRowAction={{
            title: "Voeg toe aan geselecteerde monitoringbuizen",
            action: addTube,
            icon: <ArrowUpIcon />,
          }}
          tableData={availableTubes}
        />
      )}
    </Flex>
  );
};

type PathFile = { path: string; file: globalThis.File } | null;

const ExcelMeasuringpoints: React.FC<FieldProps<RegistrationMeasuringPoint[]>> = ({ onChange }) => {
  const toast = useToast();
  const showToast = (title: string, status: "error" | "success" | "info") => {
    toast({
      title: title,
      status: status,
      duration: 9000,
      isClosable: true,
    });
  };
  const [file, setFile] = useState<PathFile>(null);

  const handleFileChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    if (!target.files) return;
    const file = target.files[0];

    const reader = new FileReader();

    reader.onload = (event) => {
      const workbook = XLSX.read(event.target?.result, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet);

      const measuringPoints: RegistrationMeasuringPoint[] = [];
      for (const dataPoint of sheetData) {
        const d = dataPoint as Record<string, unknown>;
        if (
          !(dataPoint instanceof Object) ||
          !("measuringPointCode" in dataPoint) ||
          !("broId" in dataPoint) ||
          !("tubeNumber" in dataPoint) ||
          typeof d.measuringPointCode !== "string" ||
          typeof d.broId !== "string" ||
          typeof d.tubeNumber !== "number"
        ) {
          return showToast("Er is een fout in het geuploade excel bestand", "error");
        }
        measuringPoints.push({
          measuringPointCode: d.measuringPointCode,
          broId: d.broId,
          tubeNumber: d.tubeNumber + "",
        });
      }

      onChange(measuringPoints);
    };
    reader.readAsBinaryString(file);

    setFile({ path: target.value, file });
  };

  return (
    <Flex position="relative" w="fit-content" h="fit-content" ml="auto" alignItems="center">
      <Box mt={5} mb={5} mr={10}>
        <FormLabel mb={0} whiteSpace="nowrap">
          Meetpunten bestand
        </FormLabel>
        <Flex alignItems="center" mb={1}>
          <Input
            isRequired={false}
            pt={1}
            name="file"
            id="file"
            h="input"
            type="file"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            value={file ? file.path : ""}
            onChange={handleFileChanged}
          />
          <Popover isLazy>
            <PopoverTrigger>
              <IconButton
                aria-label={"Toelichting"}
                icon={<InfoOutlineIcon />}
                variant="accent"
                tabIndex={0}
              />
            </PopoverTrigger>
            <Box pos="relative" zIndex={9999}>
              <PopoverContent w="fit-content" maxW="500px">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody>
                  <Text pb={5}>
                    Gebruik deze optie om een bulk buizen aan een nieuw GMN te koppelen. Zorg ervoor
                    dat u het format in het volgende template hanteert:
                  </Text>
                  <Button
                    variant="primary"
                    onClick={() =>
                      downloadFile(
                        "/files/gmnMeasuringpointsExcelTemplate.xlsx",
                        "gmnMeasuringpointsExcelTemplate.xlsx"
                      )
                    }
                  >
                    Download template
                  </Button>
                </PopoverBody>
              </PopoverContent>
            </Box>
          </Popover>
        </Flex>
      </Box>
    </Flex>
  );
};

/**
 * Custom RJSF field for selecting monitoring tubes (GMN form).
 * - Syncs redux selectedMonitoringTubes → RJSF formData on every change
 * - Renders the Overview table with selectMonitoringTubes enabled
 */
export const SelectMeasuringPoints = (_props: FieldProps) => {
  const props = _props as FieldProps<RegistrationMeasuringPoint[]>;

  return (
    <GmwView
      mode="map"
      excelMeasuringPoints={<ExcelMeasuringpoints {...props} />}
      monitoringTubeSelectionTable={<SelectMonitoringTubesTables {...props} />}
    />
  );
};
